import React from 'react';
import {
  Button,
  Card,
  Drawer,
  DrawerContent,
  ScrollArea,
  Image,
} from '@city-sage/aurum-avis-labs-components';
import { useQuery } from '@tanstack/react-query';
import { useRepository } from '@city-sage/aurum-avis-labs-components';
import { useTranslation } from 'react-i18next';
import {
  MapPin,
  Clock,
  Calendar,
  Navigation,
  X,
  ArrowRight,
} from 'lucide-react';
import { Stop, Day } from '@city-sage/core-client';
import { Link, useParams } from '@tanstack/react-router';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { useFormattedDate } from '../../lib/dateUtils';
import { useNavigationModalHandler } from '../../lib/hooks/useNavigation';
import { NavigationProviderModal } from '../actions/NavigationProviderModal';
import { useReactions } from '../../lib/useReactions';
import { LikeButton } from '../actions/LikeButton';
import { CommentButton } from '../actions/CommentButton';
import { ShareButton } from '../actions/ShareButton';
import { getDayColor } from '../../lib/constants/dayColors';
import { useLegsByItinerary } from '../../hooks/useLegsByItinerary';
import { getTravelModeIcon, formatDuration } from '../../lib/travelModeUtils';
import { TripMap } from '../TripMap';
import { QueryKeys } from '../../lib/QueryKeys';

interface StopPreviewSheetProps {
  stop: Stop | null;
  itineraryId?: string;
  currentDay?: Day;
  isOpen: boolean;
  onClose: () => void;
}

export const StopPreviewSheet: React.FC<StopPreviewSheetProps> = ({
  stop,
  itineraryId,
  currentDay,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { formatDateShort } = useFormattedDate();
  const { api } = useRepository();
  const navigation = useNavigationModalHandler();
  const params = useParams({ strict: false });

  // Get reactions (likes) for the stop
  const { isLiked, likeCount, toggleLike, isPending: isLikePending } = useReactions({
    reactableType: 'stop',
    reactableId: stop?.id || ''
  });

  // Fetch all days if we have itineraryId
  const { data: allDays } = useQuery({
    queryKey: QueryKeys.itinerary.days(itineraryId!),
    queryFn: () =>
      api.findAllItineraryDay({
        limit: 1000,
        itineraryId: itineraryId!,
        sort: JSON.stringify(['number', 'ASC']),
      }),
    enabled: !!itineraryId,
  });

  // Find the day for this stop if not provided
  const dayForStop = React.useMemo(() => {
    if (currentDay) return currentDay;
    if (!stop || !allDays) return undefined;
    return allDays.find((day) => stop.dayIds?.includes(day.id));
  }, [stop, allDays, currentDay]);

  // Fetch legs for the itinerary to find travel info
  const { data: legs = [] } = useLegsByItinerary({
    itineraryId: itineraryId || '',
    enabled: !!itineraryId,
  });

  // Find the leg that leads TO this stop
  const legToThisStop = React.useMemo(() => {
    if (!stop) return undefined;
    return legs.find(leg => leg.toStopId === stop.id);
  }, [legs, stop]);

  // Fetch posts for this stop
  const { data: stopPosts = [] } = useQuery({
    queryKey: QueryKeys.post.byPostable('stop', stop?.id || ''),
    queryFn: async () => {
      if (!stop?.id) return [];
      return api.findAllPost({
        limit: 1000,
        filter: JSON.stringify({
          postableId: stop.id,
          postableType: 'stop',
        }),
      });
    },
    enabled: !!stop?.id,
  });

  // Get traveler comments (posts with text)
  const travelerComments = React.useMemo(() => {
    return stopPosts
      .filter((post) => post.text && post.text.trim().length > 0)
      .slice(0, 5)
      .map((post) => ({
        id: post.id,
        text: post.text!,
        author: post.account,
        createdAt: post.createdAt,
      }));
  }, [stopPosts]);

  const formatTime = (time: string | null | undefined) => {
    if (!time) return '';
    const timePattern = /^\d{1,2}:\d{2}(:\d{2})?$/;
    if (timePattern.test(time)) {
      return time;
    }
    try {
      const date = new Date(time);
      if (isNaN(date.getTime())) {
        return time;
      }
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch {
      return time;
    }
  };

  const formatDurationLocal = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}min`;
    if (hours > 0) return `${hours}h`;
    return `${mins}min`;
  };

  // Handle navigation directions
  const handleNavigate = () => {
    if (stop?.place?.coordinates) {
      navigation.navigate({
        latitude: stop.place.coordinates.latitude,
        longitude: stop.place.coordinates.longitude,
        title: stop.title || stop.place.name || 'Location',
        address: stop.place.name || 'Location',
      });
    }
  };

  if (!stop) return null;

  const dayColor = dayForStop ? getDayColor(dayForStop.number) : null;

  // Check if we're already on the stop page
  const isOnStopPage = params.itineraryId === itineraryId && params.stopId === stop?.id;

  return (
    <>
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="h-[95dvh] flex flex-col overflow-hidden">
        <ScrollArea className="flex-1 mt-4 w-full min-w-0 overflow-x-hidden mx-auto max-w-6xl">
            {/* Hero Image Section */}
          {stop.imageUrl || stop.image ? (
            <div className="relative h-96 md:h-[32rem] w-full overflow-hidden">
              <Image
                src={stop.imageUrl}
                image={stop.image}
                alt={stop.title || 'Stop'}
                className="w-full h-full"
                imageClassName="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
 

              {/* Hero Content Overlay */}
              <div className="absolute inset-0 z-10 flex items-end">
                <div className="w-full px-4 pb-6 md:px-10 md:pb-8">
                  <div className="mx-auto max-w-4xl space-y-4">
                    {/* Title with Day Number Badge */}
                    <div className="space-y-2">
                      {stop.index && (
                        <div className="inline-flex items-center gap-1.5 rounded-sm bg-white/95 px-2 py-1">
                          <div 
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: dayColor?.hex || '#6366f1' }}
                          />
                          <span className="text-xs font-medium text-gray-900">Stop {stop.index}</span>
                        </div>
                      )}
                      <h1 className="text-3xl md:text-5xl font-bold leading-tight text-white text-balance">
                        {stop.title || stop.place?.name || 'Stop'}
                      </h1>
                    </div>

                    {/* Highlight/Description */}
                    {stop.highlight && (
                      <p className="max-w-2xl text-base text-white/90 leading-relaxed md:text-lg">
                        {stop.highlight}
                      </p>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-2">
                      <LikeButton
                        isLiked={isLiked}
                        likeCount={likeCount}
                        onToggleLike={toggleLike}
                        isPending={isLikePending}
                        theme="dark"
                        layout="horizontal"
                      />
                      <CommentButton
                        postableType="stop"
                        postableId={stop.id}
                        theme="dark"
                        layout="horizontal"
                      />
                      {itineraryId && (
                        <ShareButton
                          url={`${window.location.origin}/itineraries/${itineraryId}/stops/${stop.id}`}
                          title={stop.title || stop.place?.name || 'CitySage Stop'}
                          description={stop.highlight || stop.description || 'Check out this amazing travel stop!'}
                          hashtags={[
                            'CitySage',
                            'Travel',
                            'Stop',
                            stop.place?.address?.city || 'Adventure',
                          ]}
                          theme="dark"
                          layout="horizontal"
                          shareMethod="dialog"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 py-6 text-left border-b border-border">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="space-y-2 mb-2">
                    {stop.index && (
                      <div className="inline-flex items-center gap-1.5 rounded-sm bg-muted px-2 py-1">
                        <div 
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: dayColor?.hex || '#6366f1' }}
                        />
                        <span className="text-xs font-medium text-foreground">Stop {stop.index}</span>
                      </div>
                    )}
                    <h1 className="text-2xl font-bold text-gray-900">
                      {stop.title || stop.place?.name || 'Stop'}
                    </h1>
                  </div>
                  {stop.highlight && (
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {stop.highlight}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="bg-black/40 hover:bg-black/60 backdrop-blur-md text-white border border-white/20"
                  onClick={onClose}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
          )}
            <div className="px-4 md:px-10 py-8 space-y-12 w-full min-w-0 max-w-full overflow-x-hidden">
              {/* About This Stop */}
              {stop.description && (
                <section className="w-full max-w-full overflow-x-hidden">
                  <div>
                    <h2 className="mb-2 text-sm uppercase tracking-wider text-muted-foreground">
                      About This Stop
                    </h2>
                    <div className="h-px w-12 bg-accent" />
                  </div>
                  <div className="prose prose-neutral max-w-none overflow-x-hidden mt-6">
                    <MarkdownRenderer
                      content={stop.description}
                      className="text-foreground/70 leading-relaxed text-lg"
                    />
                  </div>
                </section>
              )}

              {/* Details Grid */}
              <div className="grid md:grid-cols-3 gap-8 w-full max-w-full">

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Schedule Card */}
                  {(stop.startTime || stop.durationMinutes) && (
                    <Card className="border border-border rounded-sm p-6">
                      <h3 className="text-sm uppercase tracking-wider text-muted-foreground mb-4">
                        Schedule
                      </h3>
                      <div className="space-y-4">
                        {stop.startTime && (
                          <div className="flex items-start gap-3">
                            <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                            <div className="flex-1">
                              <div className="text-sm text-muted-foreground mb-1">Visit Time</div>
                              <div className="font-medium">
                                {formatTime(stop.startTime)}
                                {stop.departureTime && ` - ${formatTime(stop.departureTime)}`}
                              </div>
                            </div>
                          </div>
                        )}
                        {stop.durationMinutes && (
                          <div className="flex items-start gap-3">
                            <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                            <div className="flex-1">
                              <div className="text-sm text-muted-foreground mb-1">Duration</div>
                              <div className="font-medium">{formatDurationLocal(stop.durationMinutes)}</div>
                            </div>
                          </div>
                        )}
                        {legToThisStop && legToThisStop.travelTimeMinutes && legToThisStop.travelTimeMinutes > 0 && (
                          <div className="pt-3 border-t border-border">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              {(() => {
                                const TravelModeIcon = getTravelModeIcon(legToThisStop.travelMode);
                                return <TravelModeIcon className="h-4 w-4" />;
                              })()}
                              <span>
                                ~{formatDuration(legToThisStop.travelTimeMinutes)} travel from previous stop
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}

                  {/* Location Card */}
                  <Card className="border border-border rounded-sm p-6">
                    <h3 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Location
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <div className="font-medium mb-1">{stop.place?.name || 'Location'}</div>
                        <div className="text-sm text-muted-foreground space-y-0.5">
                          {stop.place?.address?.addressLine1 && (
                            <div>{stop.place.address.addressLine1}</div>
                          )}
                          {stop.place?.address?.city && (
                            <div>
                              {stop.place.address.city}
                              {stop.place.address.postalCode && `, ${stop.place.address.postalCode}`}
                            </div>
                          )}
                          {stop.place?.address?.countryName && (
                            <div>{stop.place.address.countryName}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Day Context */}
                  {dayForStop && (
                    <Card className="border border-border rounded-sm p-6">
                      <h3 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: dayColor?.hex }}
                        />
                        Part of Day {dayForStop.number}
                      </h3>
                      <div className="space-y-2">
                        <div className="font-medium">
                          {dayForStop.title || `Day ${dayForStop.number}`}
                        </div>
                        {itineraryId && (
                          <Link
                            to="/itineraries/$itineraryId/days/$dayId"
                            params={{ itineraryId, dayId: dayForStop.id }}
                            className="inline-block mt-3"
                          >
                            <Button variant="ghost" size="sm" className="h-8 px-3 text-xs">
                              View Full Day
                            </Button>
                          </Link>
                        )}
                      </div>
                    </Card>
                  )}
                </div>
              </div>

              {/* Map Section */}
              {stop.place?.coordinates && (
                <section className="border-t border-border pt-8">
                  <div className="mb-6">
                    <h2 className="text-2xl md:text-3xl font-light mb-2">Location</h2>
                    <p className="text-foreground/60">
                      Find {stop.title || stop.place?.name || 'this stop'} on the map
                    </p>
                  </div>
                  <div className="relative aspect-[1/1] md:aspect-[16/9] min-h-[400px] md:h-auto w-full overflow-hidden bg-muted rounded-sm">
                  <TripMap
                      stops={[stop]}
                      days={dayForStop && dayForStop.stopIds ? [dayForStop] : []}
                      legs={[]}
                      className="h-full"
                      interactive={true}
                    />
                  </div>
                </section>
              )}
            </div>
          </ScrollArea>

          {/* Footer Actions */}
          <div className="p-4 md:p-6 bg-background border-t border-border">
            <div className="space-y-3">
              {itineraryId && (
                isOnStopPage ? (
                  <Button 
                    size="sm" 
                    className="w-full"
                    onClick={onClose}
                  >
                    Close Preview
                  </Button>
                ) : (
                  <Button 
                    size="sm" 
                    className="w-full"
                    asChild
                  >
                    <Link
                      to="/itineraries/$itineraryId/stops/$stopId"
                      params={{ itineraryId, stopId: stop.id }}
                    >
                      View Full Page
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                )
              )}
              <Button
                onClick={handleNavigate}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Navigation className="w-4 h-4 mr-2" />
                {t('itinerary.stopPage.getDirections')}
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Navigation Provider Modal */}
      <NavigationProviderModal
        isOpen={navigation.isModalOpen}
        onClose={() => navigation.setIsModalOpen(false)}
        onSelect={navigation.handleProviderChoice}
        destinationName={stop?.title || stop?.place?.name || 'Location'}
      />
    </>
  );
};
